import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { ToolbarModule } from 'primeng/toolbar';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ContractService } from '../contract.service';
import { HttpParams } from '@angular/common/http';
import { forkJoin, Observable, Subscription } from 'rxjs';

import { Customer } from '../../master-data/customers/customers.model';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';

import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { Contract } from '../contract.model';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AppUser } from '../../users/user.model';
import { AuthenticationService } from '../../services/authentication.service';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ProcessStatus } from '../../enums/processStatus';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { ContractType } from '../contract-types';
import { ContractStatus, ContractStatusKey } from '../contract-status';
import { SupplierSite } from '../../master-data/suppliers/supplierSite/supplierSite.model';
import { SupplierSiteService } from '../../master-data/suppliers/supplierSite/supplierSite.service';
@Component({
  selector: 'app-contract-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    CalendarModule,
    TagModule,
    ToastModule,
    ToolbarModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    ChipModule,
    DropdownModule,
    IftaLabelModule,
    ConfirmDialog,
    DatePicker,
    ProgressSpinner,
  ],

  providers: [MessageService, ConfirmationService],
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.css'],
})
export class ContractListComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  contracts: Contract[] = [];
  totalRecords = 0;
  isLoading: boolean = false;
  page: number = 0;
  rows: number = 25;
  params: HttpParams = new HttpParams();

  filterForm!: FormGroup;
  private filterSub!: Subscription;
  isSubmitted: boolean = false;
  user: AppUser | null = null;
  user$: Observable<AppUser | null>;

  // Options for filter dropdowns
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];
  suppliers: Supplier[] = [];
  filteredSuppliers: Supplier[] = [];
  supplierSites: SupplierSite[] = [];
  filteredSupplierSites: SupplierSite[] = [];

  CustomerfilterValue: string = '';
  SupplierfilterValue: string = '';
  BusinessfilterValue: string = '';
  SupplierSitefilterValue: string = '';

  contractTypes: { label: string; value: ContractType }[] = Object.values(
    ContractType
  ).map((t) => ({ label: t, value: t }));

  processStatus = Object.keys(ContractStatus)
    .map((key) => key as ContractStatusKey)
    .map((key) => ({
      label: ContractStatus[key].name,
      value: ContractStatus[key].id,
    }));

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private contractService: ContractService,
    private messageService: MessageService,
    private customerService: CustomersService,
    private businessService: BusinessService,
    private supplierService: SuppliersService,
    private supplierSiteService: SupplierSiteService,

    private fb: FormBuilder
  ) {
    this.user$ = this.authService.getAuthState();
  }

  ngOnInit() {
    this.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      } else {
        this.user = null;
      }
    });

    this.buildFilterForm();
    this.loadDropdownData();
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      contractName: [''],
      contractNo: [''],
      startDateActive: [null],
      endDateActive: [null],
      customer: [null],
      business: [null],
      vendor: [null],
      vendorSite: [null],
      items: [null],
      contractType: [null],
      contractStatus: [null],
      batchNumber: [''],
    });
  }

  loadContracts(
    event?: TableLazyLoadEvent,
    isExport = false,
    callback?: (contracts: Contract[]) => void
  ): void {
    this.isLoading = true;
    if (isExport) {
      this.params = this.params.set('page', 0);
      this.params = this.params.set('size', 2000000);
    } else {
      this.params = this.params.set(
        'page',
        event?.first
          ? (event.first / (event.rows || this.rows)).toString()
          : this.page.toString()
      );
      this.params = this.params.set(
        'size',
        event?.rows ? event.rows.toString() : this.rows.toString()
      );
    }

    if (event?.sortField) {
      this.params = this.params.set(
        'sort',
        `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}`
      );
    } else {
      this.params = this.params.set('sort', 'contractId,desc');
    }

    this.contractService.searchContracts(this.params).subscribe({
      next: (page) => {
        this.contracts = page.content;
        this.totalRecords = page.totalElements;
        this.isLoading = false;
        if (callback) {
          callback(this.contracts);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load contracts',
        });
      },
    });
  }

  onSearch() {
    this.dt.reset();
  }

  onReset() {
    this.filterForm.reset();
    this.dt.reset();
  }

  submitForm() {
    this.isSubmitted = true;
    if (this.filterForm.valid) {
      this.isLoading = true;

      const filterFormClone = this.filterForm.value;

      const filters: any = {
        contractName: filterFormClone.contractName,
        contractNo: filterFormClone.contractNo,
        contractType: filterFormClone.contractType,
        contractStatus: filterFormClone.contractStatus,
        startDateActive: filterFormClone.startDateActive
          ? this.formatDateToLocal(filterFormClone.startDateActive)
          : null,
        endDateActive: filterFormClone.endDateActive
          ? this.formatDateToLocal(filterFormClone.endDateActive)
          : null,
        customer: filterFormClone.customer?.id,
        business: filterFormClone.business?.id,
        supplier: filterFormClone.vendor?.id,
        supplierSite: filterFormClone.vendorSite?.id,
        items: filterFormClone.items?.id,
        batchNumber: filterFormClone.batchNumber,
      };    

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== '' && value !== undefined) {
          this.params = this.params.set(key, value);
        } else {
          this.params = this.params.delete(key);
        }
      });
      this.dt.reset();
    }
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadDropdownData(): void {
    forkJoin({
      customers: this.customerService.getAllCustomers({ page: 0, size: 10000 }),
      businesses: this.businessService.getAllBusinesss({ page: 0, size: 10000 }),
      suppliers: this.supplierService.getAllSuppliers({ page: 0, size: 10000 }),
      supplierSites: this.supplierSiteService.getAllSupplierSites({
        page: 0,
        size: 10000,
      }),
    }).subscribe({
      next: (data) => {
        //no need to filter supplier sites
        this.supplierSites = data.supplierSites.content;
        this.filteredSupplierSites = data.supplierSites.content;

        if (this.user?.roles[0]?.roleName === 'Admin') {
          this.customers = data.customers.content;
          this.filteredCustomers = data.customers.content;
          this.businesses = data.businesses.content;
          this.filteredBusinesses = data.businesses.content;
          this.suppliers = data.suppliers.content;
          this.filteredSuppliers = data.suppliers.content;
        } else {
          if (!this.user?.customerIds || this.user.customerIds.length === 0) {
            this.customers = data.customers.content;
            this.filteredCustomers = data.customers.content;
          } else {
            this.customers = data.customers.content;
            this.filteredCustomers =
              this.user?.customerIds && this.user.customerIds.length > 0
                ? this.customers.filter((c) =>
                    this.user?.customerIds?.includes(c.id)
                  )
                : [];
          }

          this.businesses = data.businesses.content;
          this.filteredBusinesses =
            this.user?.businessIds && this.user.businessIds.length > 0
              ? this.businesses.filter((b) =>
                  this.user?.businessIds?.includes(b.id)
                )
              : [];

          this.suppliers = data.suppliers.content;
          this.filteredSuppliers =
            this.user?.supplierIds && this.user.supplierIds.length > 0
              ? this.suppliers.filter((s) =>
                  this.user?.supplierIds?.includes(s.id)
                )
              : [];
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load filter options.',
        });
        console.error('Error isLoading dropdown data:', err);
      },
    });
  }

  filterCustomers(event: any) {
    this.CustomerfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(
        (customer) =>
          customer.customerName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          customer.customerNumber
            .toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
    }
  }

  filterBusinesses(event: any) {
    this.BusinessfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(
        (business) =>
          business.businessName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (business.businessCode &&
            business.businessCode
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSuppliers(event: any) {
    this.SupplierfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredSuppliers = this.suppliers;
    } else {
      this.filteredSuppliers = this.suppliers.filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplier.supplierNumber
            ? supplier.supplierNumber
                .toLowerCase()
                .includes(event.target.value.toLowerCase())
            : false)
      );
    }
  }

  filterSupplierSites(event: any) {
    this.SupplierSitefilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredSupplierSites = this.supplierSites;
    } else {
      this.filteredSupplierSites = this.supplierSites.filter(
        (supplierSite) =>
          supplierSite.supplierSiteName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplierSite.supplierSiteNumber
            ? supplierSite.supplierSiteNumber
                .toLowerCase()
                .includes(event.target.value.toLowerCase())
            : false)
      );
    }
  }

  onLazyLoad(event: any) {
    this.loadContracts(event, false);
  }

  goToDetailsList(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['rebate', 'multi-export'] } },
    ]);
  }

  ViewContract(contract: Contract): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['rebate', 'view', contract.contractId] } },
    ]);
  }

  EditContract(contract: Contract): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['rebate', 'edit', contract.contractId] } },
    ]);
  }

  clear(table: Table): void {
    table.clear();
  }

  exportExcel() {
    this.loadContracts(undefined, true, (contract: Contract[]) => {
      const dataToExport = contract.map((contract) => ({
        'Contract ID': contract.contractId,
        'Contract Number': contract.contractNo,
        'Contract Name': contract.contractName,
        Status: contract.contractStatus?.statusName,
        'Actual Start Date': contract.startDateActive
          ? new Date(contract.startDateActive).toLocaleDateString()
          : '',
        'Actual End Date': contract.endDateActive
          ? new Date(contract.endDateActive).toLocaleDateString()
          : '',
        'Customer #': contract.customer?.customerNumber,
        'Customer Name': contract.customer?.customerName,
        // 'Business': contract.business?.businessName,
        // 'Supplier': contract.supplier?.supplierName,
        Note: contract.contractNote1,
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contracts');
      XLSX.writeFile(wb, 'rebate_Export.xlsx');
    });
  }
}
